import * as THREE from 'three';
import { CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';
import type { Quality } from '../core/settings';
import type { GameState } from '../game/state';
import { CameraRig } from './CameraRig';
import { Critters } from './Critters';
import { FarmPlots, type PlotPick } from './FarmPlots';
import { FarmProps, type PropPick } from './FarmProps';
import { FloatingText } from './FloatingText';
import { windTime } from './materials';
import { Particles } from './Particles';
import { Sky } from './Sky';
import { Terrain } from './Terrain';
import { Weather } from './Weather';

export type PickResult = PlotPick | PropPick | { kind: 'ground'; point: THREE.Vector3 } | null;

const FOV = 50;

/** Owns the three.js renderer, scene and every visual subsystem. */
export class WorldRenderer {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(FOV, 1, 0.3, 4000);
  readonly rig = new CameraRig(this.camera);
  readonly renderer: THREE.WebGLRenderer;
  readonly labels = new CSS2DRenderer();
  readonly plots: FarmPlots;
  readonly props: FarmProps;
  readonly particles = new Particles();
  readonly floating: FloatingText;
  readonly sky: Sky;
  readonly weather = new Weather();

  private readonly critters: Critters;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pickables: THREE.Object3D[];
  private readonly groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly resizeObserver: ResizeObserver;
  private smokeTimer = 0;
  private sparkleTimer = 0;
  private elapsed = 0;

  constructor(
    private readonly container: HTMLElement,
    quality: Quality,
  ) {
    const high = quality === 'high';
    this.renderer = new THREE.WebGLRenderer({
      antialias: high,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, high ? 2 : 1.25));
    this.renderer.shadowMap.enabled = high;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.domElement.className = 'world-canvas';
    container.appendChild(this.renderer.domElement);

    this.labels.domElement.className = 'world-labels';
    container.appendChild(this.labels.domElement);

    this.sky = new Sky(this.scene, high);
    this.scene.add(new Terrain(quality).group);
    this.props = new FarmProps();
    this.scene.add(this.props.group);
    this.plots = new FarmPlots(high);
    this.scene.add(this.plots.group);
    this.critters = new Critters(this.props.beehivePosition);
    this.scene.add(this.critters.group);
    this.scene.add(this.weather.group);
    this.scene.add(this.particles.points);
    this.floating = new FloatingText(this.scene);

    this.pickables = [...this.plots.pickables, ...this.props.pickables];

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.resize();
  }

  get canvas(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  resize(): void {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    this.camera.aspect = width / height;
    // Keep the farm comfortably in view on tall phone screens.
    this.camera.fov = width < height ? FOV + 14 : FOV;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
    this.labels.setSize(width, height);
    this.particles.setViewport(this.renderer.domElement.height, this.camera.fov);
  }

  /** Raycasts from normalised device coordinates into the world. */
  pick(ndcX: number, ndcY: number): PickResult {
    this.raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.camera);
    const hit = this.raycaster.intersectObjects(this.pickables, false)[0];
    if (hit) return hit.object.userData.pick as PlotPick | PropPick;
    const point = new THREE.Vector3();
    if (this.raycaster.ray.intersectPlane(this.groundPlane, point))
      return { kind: 'ground', point };
    return null;
  }

  update(dt: number, state: GameState, focus: THREE.Vector3): void {
    this.elapsed += dt;
    windTime.value = this.elapsed;

    this.rig.update(dt, focus);
    this.weather.update(
      dt,
      state.weather.raining,
      state.weather.rainbowLeft,
      this.sky.night,
      this.rig.target,
    );
    this.sky.update(state.dayTime, this.weather.rain, this.camera, this.rig.target);
    const night = this.sky.night;

    this.props.update(dt, this.elapsed, state, night);
    this.plots.update(dt, this.elapsed, state, night);
    this.critters.update(dt, this.elapsed, state, night);

    this.smokeTimer -= dt;
    if (this.smokeTimer <= 0) {
      this.smokeTimer = 0.45;
      this.particles.smoke(this.props.chimneyTop);
    }
    this.sparkleTimer -= dt;
    if (this.sparkleTimer <= 0) {
      this.sparkleTimer = 0.6;
      for (const plotId of this.plots.goldenPlots()) {
        this.particles.sparkles(this.plots.plotTop(plotId).setY(0.9), 2);
      }
    }

    this.particles.update(dt);
    this.floating.update(dt);
  }

  /** Sprinkler spray from every sprinkler head. */
  spray(): void {
    for (const spot of this.props.sprinklerSpots) this.particles.water(spot);
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
    this.labels.render(this.scene, this.camera);
  }

  dispose(): void {
    this.resizeObserver.disconnect();
    this.renderer.dispose();
    this.renderer.domElement.remove();
    this.labels.domElement.remove();
  }
}
